import React, { useState } from 'react';
import { Box } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTasksTab } from '../../../hooks/Tasks/useTasksTab';
import { PRIMARY_COLOR } from '../../../components/Tasks/constants';
import { formatRelativeTime } from '../../../components/shared/helpers/formatRelativeTime';
import { tokens } from "../../../theme/tokens";

function targetText(value: any, label?: string): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return value.name ?? value.text ?? label ?? '';
  return label ?? '';
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      fontSize: '10px',
      fontWeight: 700,
      color: tokens.color.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      display: 'block',
      marginBottom: '10px',
    }}
  >
    {children}
  </span>
);

const MetaRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', mb: '8px' }}>
    <span style={{ fontSize: '13px', color: tokens.color.text.secondary, flexShrink: 0 }}>{label}:</span>
    <span style={{ fontSize: '13px', fontWeight: 600, color: tokens.color.text.primary, textAlign: 'right', minWidth: 0, wordBreak: 'break-word' }}>
      {value}
    </span>
  </Box>
);

const ColDivider = () => (
  <Box sx={{ width: '1px', backgroundColor: tokens.color.surface.subtle, alignSelf: 'stretch', flexShrink: 0 }} />
);

const CaseSummaryPanel: React.FC = () => {
  const { filteredTasks, pois, activePoiId, openNewTaskSheet } = useTasksTab();
  const [notes, setNotes] = useState('');

  const activePoi = pois.find((p) => p.id === activePoiId);

  const doneTasks = filteredTasks.filter((t) => t.status === 'DONE').length;
  const winningLeads = filteredTasks.reduce((sum, t) => sum + t.primary_target_ids.length, 0);

  const lastActivity = filteredTasks.reduce<string | null>((latest, task) => {
    return !latest || task.updated_at > latest ? task.updated_at : latest;
  }, null);

  const allTargets = filteredTasks.flatMap((t) => t.targets);
  const workedPhones    = allTargets.filter((t) => t.type === 'PHONE'   && t.status === 'WORKED');
  const workedAddresses = allTargets.filter((t) => t.type === 'ADDRESS' && t.status === 'WORKED');
  // DOING profiles are intentionally included — a profile under active review is surfaced
  // as a "Possible match" in the panel rather than being hidden until fully confirmed.
  const workedUrls      = allTargets.filter((t) => t.type === 'PROFILE' && (t.status === 'WORKED' || t.status === 'DOING'));
  const workedPeople    = filteredTasks
    .filter((t) => t.group_key === 'RELATIVES_ASSOCIATES')
    .flatMap((t) => t.targets.filter((tgt) => tgt.status === 'WORKED'));

  const hasEntities =
    workedPhones.length > 0 || workedAddresses.length > 0 ||
    workedUrls.length > 0   || workedPeople.length > 0;

  let suggestion: { title: string; description: string } | null = null;
  if (filteredTasks.length === 0) {
    suggestion = { title: 'Create First Task', description: 'Start with a data provider or phone lookup.' };
  } else if (workedPhones.length > 0 && workedAddresses.length === 0) {
    suggestion = { title: 'Run Address Verification', description: 'Phone confirmed. Verify current address.' };
  } else if (
    workedAddresses.length > 0 &&
    !filteredTasks.some((t) => t.group_key === 'FIELD_RESOURCES' && t.status === 'DONE')
  ) {
    suggestion = { title: 'Schedule Field Visit', description: 'Address confirmed. Consider field investigation.' };
  }

  return (
    <Box sx={{ display: 'flex', overflow: 'hidden' }}>
      {/* Col 1: Stats */}
      <Box sx={{ flex: 2, px: '18px', py: '14px', minWidth: 0 }}>
        <SectionLabel>Overview</SectionLabel>
        <MetaRow label="Objective"       value={activePoi?.name ?? 'All POIs'} />
        <MetaRow label="Tasks Completed" value={`${doneTasks} / ${filteredTasks.length}`} />
        <MetaRow label="Winning Leads"   value={winningLeads} />
        <MetaRow
          label="Last Activity"
          value={lastActivity ? formatRelativeTime(lastActivity) : '—'}
        />
      </Box>

      <ColDivider />

      {/* Col 2: Entities Discovered */}
      <Box sx={{ flex: 2, px: '18px', py: '14px', minWidth: 0 }}>
        <SectionLabel>Entities Discovered</SectionLabel>
        {!hasEntities && (
          <span style={{ fontSize: '12px', color: tokens.color.border.strong }}>No entities discovered yet.</span>
        )}
        {workedPeople.length > 0 && (
          <MetaRow label="People" value={targetText(workedPeople[0].value, workedPeople[0].label)} />
        )}
        {workedPhones.length > 0 && (
          <MetaRow
            label="Phones"
            value={
              workedPhones.length > 1
                ? `${targetText(workedPhones[0].value, workedPhones[0].label)} +${workedPhones.length - 1}`
                : targetText(workedPhones[0].value, workedPhones[0].label)
            }
          />
        )}
        {workedAddresses.length > 0 && (
          <MetaRow label="Addresses" value={targetText(workedAddresses[0].value, workedAddresses[0].label)} />
        )}
        {workedUrls.length > 0 && (
          <MetaRow
            label="Profiles"
            value={
              workedUrls[0].status === 'DOING'
                ? `Possible match — ${targetText(workedUrls[0].value, workedUrls[0].label)}`
                : targetText(workedUrls[0].value, workedUrls[0].label)
            }
          />
        )}
      </Box>

      {/* Col 3: Suggested Next Step (only when suggestion exists) */}
      {suggestion && (
        <>
          <ColDivider />
          <Box sx={{ flex: 1.5, px: '18px', py: '14px', minWidth: 0 }}>
            <SectionLabel>Suggested Next Step</SectionLabel>
            <span style={{ fontSize: '13px', fontWeight: 600, color: tokens.color.text.primary, display: 'block', marginBottom: '4px' }}>
              {suggestion.title}
            </span>
            <span style={{ fontSize: '12px', color: tokens.color.text.secondary, display: 'block', marginBottom: '12px', lineHeight: 1.5 }}>
              {suggestion.description}
            </span>
            <button
              type="button"
              onClick={openNewTaskSheet}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                fontWeight: 600,
                color: PRIMARY_COLOR,
                backgroundColor: tokens.color.surface.default,
                border: `1px solid ${tokens.color.border.default}`,
                borderRadius: '6px',
                padding: '6px 12px',
                cursor: 'pointer',
                transition: 'all 0.12s ease',
              }}
            >
              Start Task <ArrowForwardIcon style={{ fontSize: '13px' }} />
            </button>
          </Box>
        </>
      )}

      <ColDivider />

      {/* Col 4: Notes */}
      <Box sx={{ flex: 1.5, px: '18px', py: '14px', minWidth: 0 }}>
        <SectionLabel>Notes</SectionLabel>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes here..."
          style={{
            width: '100%',
            minHeight: '60px',
            fontSize: '12px',
            color: tokens.color.text.primary,
            backgroundColor: tokens.color.surface.sunken,
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: '7px',
            padding: '8px 10px',
            outline: 'none',
            resize: 'vertical',
            fontFamily: 'inherit',
            lineHeight: 1.5,
            boxSizing: 'border-box',
          }}
        />
      </Box>
    </Box>
  );
};

export default CaseSummaryPanel;
